<script setup>
import {
    EnvelopeOpenIcon,
    XMarkIcon,
    Bars3Icon,
    ChevronDownIcon,
    PhoneIcon,
} from '@heroicons/vue/24/solid'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { ref } from 'vue'
import HomeController from '@/actions/App/Http/Controllers/HomeController'
import AboutController from '@/actions/App/Http/Controllers/AboutController'
import {
    plumbing,
    heating,
    gas,
    maintenance,
    boilerReplacement,
    heatPumps,
} from '@/actions/App/Http/Controllers/ServiceController'
import { index as projectIndex } from '@/actions/App/Http/Controllers/ProjectController'
import { index as contactIndex } from '@/actions/App/Http/Controllers/ContactController'
import { usePage } from '@inertiajs/vue3'

const isOpen = ref(false)

const services = [
    {
        name: 'Vodoinstalace',
        href: plumbing(),
    },
    {
        name: 'Topenářství',
        href: heating(),
    },
    {
        name: 'Plynoinstalace',
        href: gas(),
    },
    {
        name: 'Servis / Sezonní údržba',
        href: maintenance(),
    },
    {
        name: 'Výměna kotlů',
        href: boilerReplacement(),
    },
    {
        name: 'Tepelná čerpadla',
        href: heatPumps(),
    },
]
</script>

<template>
    <header class="fixed top-0 left-0 z-50 w-full translate-y-0 opacity-100">
        <nav class="relative bg-white/90 shadow backdrop-blur-lg">
            <div class="container mx-auto px-6 py-4">
                <div class="lg:flex lg:items-center lg:justify-between">
                    <div class="flex items-center justify-between">
                        <Link
                            :href="HomeController()"
                            class="flex items-center gap-x-2"
                        >
                            <img
                                class="h-8 w-auto lg:h-7"
                                src="/images/kalianko-logo.png"
                                alt=""
                            />
                        </Link>

                        <!-- Mobile menu button -->
                        <div class="flex lg:hidden">
                            <button
                                x-cloak
                                @click="isOpen = !isOpen"
                                type="button"
                                class="text-gray-500 hover:text-gray-600 focus:text-gray-600 focus:outline-none"
                                aria-label="toggle menu"
                            >
                                <Bars3Icon
                                    :class="[isOpen ? 'hidden' : 'block']"
                                    class="size-6"
                                />

                                <XMarkIcon
                                    :class="[isOpen ? 'block' : 'hidden']"
                                    class="size-6"
                                />
                            </button>
                        </div>
                    </div>

                    <div
                        :class="[isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0']"
                        class="absolute inset-x-0 z-20 w-full bg-white px-6 py-4 transition-all duration-300 ease-in-out lg:relative lg:top-0 lg:mt-0 lg:flex lg:w-auto lg:translate-x-0 lg:items-center lg:bg-transparent lg:p-0 lg:opacity-100"
                    >
                        <div
                            class="-mx-6 flex flex-col space-y-2 px-4 lg:mx-8 lg:flex-row lg:items-center lg:space-y-0 lg:space-x-2 lg:px-0"
                        >
                            <Link
                                :href="HomeController()"
                                @click="isOpen = false"
                                class="transform rounded-full px-4 py-2 text-gray-700 transition-colors duration-300 hover:bg-gray-100 lg:mt-0"
                            >
                                Úvod
                            </Link>
                            <Link
                                :href="AboutController()"
                                @click="isOpen = false"
                                class="transform rounded-full px-4 py-2 text-gray-700 transition-colors duration-300 hover:bg-gray-100 lg:mt-0"
                            >
                                O nás
                            </Link>

                            <Popover
                                v-slot="{ close }"
                                class="relative"
                            >
                                <PopoverButton
                                    class="inline-flex transform items-center rounded-full px-4 py-2 text-gray-700 transition-colors duration-300 outline-none hover:bg-gray-100 lg:mt-0"
                                >
                                    <span>Služby</span>
                                    <ChevronDownIcon
                                        class="ml-2 size-5"
                                        aria-hidden="true"
                                    />
                                </PopoverButton>

                                <transition
                                    enter-active-class="transition ease-out duration-200"
                                    enter-from-class="opacity-0 translate-y-1"
                                    enter-to-class="translate-y-0"
                                    leave-active-class="transition ease-in duration-150"
                                    leave-from-class="translate-y-0"
                                    leave-to-class="opacity-0 translate-y-1"
                                >
                                    <PopoverPanel
                                        class="absolute left-1/2 z-10 mt-5 flex w-screen max-w-max -translate-x-1/2 bg-transparent px-4"
                                    >
                                        <div
                                            class="w-screen max-w-50 flex-auto overflow-hidden rounded-4xl bg-white text-sm/6 shadow-lg outline-1 outline-gray-900/5"
                                        >
                                            <div class="p-2">
                                                <div
                                                    v-for="item in services"
                                                    :key="item.name"
                                                    class="group relative flex gap-x-6 rounded-full p-4 hover:bg-gray-100"
                                                >
                                                    <div v-on:click="isOpen = false">
                                                        <Link
                                                            :href="item.href"
                                                            @click="close()"
                                                            class="font-semibold text-gray-900"
                                                        >
                                                            {{ item.name }}
                                                            <span class="absolute inset-0"></span>
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </PopoverPanel>
                                </transition>
                            </Popover>

                            <Link
                                :href="projectIndex()"
                                @click="isOpen = false"
                                class="transform rounded-full px-4 py-2 text-gray-700 transition-colors duration-300 hover:bg-gray-100 lg:mt-0"
                            >
                                Naše realizace
                            </Link>

                            <Link
                                :href="contactIndex()"
                                @click="isOpen = false"
                                class="transform rounded-full px-4 py-2 text-gray-700 transition-colors duration-300 hover:bg-gray-100 lg:mt-0"
                            >
                                Kontakt
                            </Link>

                            <Link
                                :href="contactIndex().url + '#demand-form'"
                                @click="isOpen = false"
                                class="transform rounded-full bg-sky-500 px-4 py-2 font-semibold text-white transition-colors duration-300 hover:bg-sky-600 lg:mt-0"
                            >
                                Objednávkový formulář
                            </Link>
                        </div>

                        <div
                            class="mt-4 flex flex-col items-start gap-2 lg:mt-0 lg:hidden lg:items-center lg:gap-4 xl:flex xl:flex-row"
                        >
                            <a
                                :href="'tel:' + usePage().props.contacts?.phone?.replace(/\s/g, '')"
                                class="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
                                :aria-label="'Telefonní číslo - ' + usePage().props.contacts?.phone"
                            >
                                <PhoneIcon class="size-4" />
                                <span>{{ usePage().props.contacts?.phone }}</span>
                            </a>
                            <a
                                :href="'mailto:' + usePage().props.contacts?.email"
                                class="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
                                :aria-label="'E-mailová adresa - ' + usePage().props.contacts?.email"
                            >
                                <EnvelopeOpenIcon class="size-4" />
                                <span>{{ usePage().props.contacts?.email }}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    </header>
</template>
